import React from "react";
import { FiMapPin, FiPhone } from "react-icons/fi";
import {
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdReceiptLong,
  MdLocalOffer,
  MdInventory2,
} from "react-icons/md";
import CustomerContactActions from "../Customer/CustomerContactActions";

const formatAmount = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const getInitials = (name) => {
  const parts = String(name || "?").trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return String(name || "?").slice(0, 2).toUpperCase();
};

const AmountValue = ({ value, valueClass = "text-white" }) => (
  <span
    className={`text-base max720:text-sm font-extrabold tabular-nums whitespace-nowrap ${valueClass}`}
  >
    <span className="font-semibold opacity-90">Rs&nbsp;</span>
    {formatAmount(value)}
  </span>
);

const QtyValue = ({ value, loading, valueClass = "text-white" }) => (
  <span
    className={`text-base max720:text-sm font-extrabold tabular-nums whitespace-nowrap ${valueClass}`}
  >
    {loading ? "…" : formatAmount(value)}
  </span>
);

const STAT_ITEMS = [
  {
    id: "totalBox",
    label: "Total Cartons",
    desc: "Total cartons (Box)",
    valueType: "qty",
    icon: MdInventory2,
    iconClass: "text-sky-200",
    valueClass: "text-sky-100",
    cardClass: "bg-sky-400/10 border border-sky-300/20",
  },
  {
    id: "total",
    label: "Total",
    desc: "Total purchase amount",
    field: "total",
    valueType: "amount",
    icon: MdReceiptLong,
    iconClass: "text-white/90",
    valueClass: "text-white",
    cardClass: "bg-white/10",
  },
  {
    id: "paid",
    label: "Paid",
    desc: "Amount paid to company",
    field: "paid",
    valueType: "amount",
    icon: MdTrendingUp,
    iconClass: "text-emerald-300",
    valueClass: "text-emerald-200",
    cardClass: "bg-white/10",
  },
  {
    id: "remaining",
    label: "Remaining",
    desc: "Still payable",
    field: "payable",
    valueType: "amount",
    icon: MdAccountBalanceWallet,
    iconClass: "text-amber-200",
    valueClass: "text-amber-100",
    cardClass: "bg-amber-400/15 border border-amber-300/30",
  },
  {
    id: "discount",
    label: "Discount",
    desc: "Total discount",
    field: "discount",
    valueType: "amount",
    icon: MdLocalOffer,
    iconClass: "text-red-300",
    valueClass: "text-red-200",
    cardClass: "bg-red-400/10 border border-red-300/20",
  },
];

const PayableCompanyDetailHero = ({
  company,
  totalBoxQty = 0,
  loadingBoxQty = false,
}) => {
  const total = Number(company.total || 0);
  const paid = Number(company.paid || 0);
  const paymentPct =
    total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : 0;

  const getStatValue = (item) => {
    if (item.id === "totalBox") return totalBoxQty;
    return company[item.field];
  };

  return (
    <div className="shrink-0 rounded-2xl bg-gradient-to-br from-[#39454F] via-[#2d363f] to-[#1f262d] text-white shadow-card mb-5 overflow-hidden">
      <div className="p-5 max720:p-4">
        <div className="flex items-start gap-4">
          <div className="shrink-0 w-14 h-14 max720:w-12 max720:h-12 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center text-lg font-bold shadow-inner">
            {getInitials(company.name)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl max720:text-lg font-extrabold leading-tight truncate">
              {company.name}
            </h2>
            {company.contact && (
              <p className="flex items-center gap-1.5 text-sm text-white/80 mt-1">
                <FiPhone size={13} className="shrink-0" />
                <span>{company.contact}</span>
              </p>
            )}
            {company.address && (
              <p className="flex items-start gap-1.5 text-xs text-white/60 mt-1 line-clamp-2">
                <FiMapPin size={12} className="shrink-0 mt-0.5" />
                <span>{company.address}</span>
              </p>
            )}
          </div>
          <div className="text-right shrink-0 max720:hidden">
            <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
              Payment rate
            </p>
            <p className="text-2xl font-extrabold text-emerald-200">{paymentPct}%</p>
          </div>
        </div>

        <div className="mt-4 hidden max720:block">
          <p className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
            Payment rate — {paymentPct}%
          </p>
        </div>

        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-white/55 mb-1.5">
            <span>Paid {paymentPct}%</span>
            <span className="tabular-nums">
              Rs&nbsp;{formatAmount(paid)} / Rs&nbsp;{formatAmount(total)}
            </span>
          </div>
          <div className="h-2 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 transition-all duration-500"
              style={{ width: `${paymentPct}%` }}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/15">
          <CustomerContactActions
            customer={company}
            variant="icon"
            reminderType="payable"
          />
        </div>
      </div>

      <div className="px-5 pb-5 max720:px-4 max720:pb-4 flex flex-col gap-2">
        {STAT_ITEMS.map(
          ({
            id,
            field,
            label,
            desc,
            valueType,
            icon: Icon,
            iconClass,
            valueClass,
            cardClass,
          }) => (
            <div
              key={id}
              className={`flex items-center justify-between gap-4 rounded-xl px-4 py-3 max720:px-3 max720:py-2.5 ${cardClass}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Icon size={15} className={`shrink-0 ${iconClass}`} />
                  <span className="text-xs font-bold uppercase tracking-wide text-white/85">
                    {label}
                  </span>
                </div>
                <p className="text-[11px] text-white/55 mt-0.5 pl-6 max720:pl-0">
                  {desc}
                </p>
              </div>
              {valueType === "qty" ? (
                <QtyValue
                  value={getStatValue({ id })}
                  loading={loadingBoxQty}
                  valueClass={valueClass}
                />
              ) : (
                <AmountValue
                  value={getStatValue(STAT_ITEMS.find((s) => s.id === id))}
                  valueClass={valueClass}
                />
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PayableCompanyDetailHero;
